import React, { useEffect, useState } from 'react';
import { MapPin, Users, Award, Heart, ChevronLeft, ChevronRight, Clapperboard } from 'lucide-react';
import { Button } from './ui/button';

export const FeaturesSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselImages = [
    {
      url: 'https://customer-assets.emergentagent.com/job_ace134d1-2e25-466b-a421-b1d4fb9eeaff/artifacts/qwfq73lv_1920px_Don%20Shiel-Bicentennial-Equestrian-Park_005.jpg',
      alt: 'Bicentennial Equestrian Park scenic pathway'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_ace134d1-2e25-466b-a421-b1d4fb9eeaff/artifacts/pdpxnu4j_6dc278e9ca967e8a0d03d8249ed96484.jpeg',
      alt: 'Camden Town at sunset with vintage car'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_ace134d1-2e25-466b-a421-b1d4fb9eeaff/artifacts/jwgtsaa7_0a9572c7bcd112f821f408cc8659fbf0.avif',
      alt: 'Unbroken movie scene - runners in vintage athletic attire'
    }
  ];

  // Auto-scroll every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Community',
      description: 'Join hundreds of runners in a celebration of fitness, friendship, and achievement.'
    },
    {
      icon: Award,
      title: 'All Levels',
      description: 'From first-timers to experienced athletes, everyone is welcome and supported.'
    },
    {
      icon: Clapperboard,
      title: 'Cinematic Backdrop',
      description: 'Run through the same cinematic landscapes featured in Unbroken, directed by Angelina Jolie.'
    },
    {
      icon: Heart,
      title: 'Country Charm',
      description: 'Experience the warmth and beauty of Camden, a true country town with heart.'
    },
    {
      icon: MapPin,
      title: 'Local Landmarks',
      description: 'A course that showcases Camden at its best.'
    },
  ];

  return (
    <section id="features" className="section-full bg-muted py-16 sm:py-24 lg:py-32">
      <div className="container-wide px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-20 items-center">
          {/* Left: Image Carousel */}
          <div className="relative h-[350px] sm:h-[450px] lg:h-[600px] scroll-reveal-left overflow-hidden">
            {/* Images */}
            {carouselImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                  index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img 
                  src={image.url} 
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </div>
            ))}
            
            {/* Carousel Navigation */}
            <button
              onClick={goToPrev}
              className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors flex items-center justify-center rounded-full"
              aria-label="Previous image"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="text-white" size={20} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors flex items-center justify-center rounded-full"
              aria-label="Next image"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="text-white" size={20} />
            </button>

            {/* Dot Indicators */}
            <div className="absolute bottom-24 sm:bottom-32 left-6 sm:left-12 z-20 flex gap-2">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-colors ${
                    index === currentSlide ? 'bg-white' : 'bg-white/40'
                  }`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Overlay text */}
            <div className="absolute bottom-6 sm:bottom-12 left-6 sm:left-12 right-6 sm:right-12 z-10">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-2 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                DISCOVER
                <br />
                CAMDEN
              </h3>
              <p className="text-white/80 text-sm sm:text-base lg:text-lg">
                Run through the heart of this beautiful country town
              </p>
            </div>
          </div>

          {/* Right: Features list */}
          <div className="scroll-reveal-right">
            <div>
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex gap-6 sm:gap-7 group mb-8 sm:mb-10 last:mb-0">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-foreground flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                        <Icon className="text-background" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg sm:text-xl font-black mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {feature.title}
                      </h4>
                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 sm:mt-10">
              <a href="https://www.visitcamden.com.au/" target="_blank" rel="noreferrer">
                <Button className="btn-standard group w-full sm:w-auto">
                  Learn more about camden
                  <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
